import { useEffect, useState } from "react";
import { debounce, isEmpty } from "lodash";
import {
  useContractsPriceTemplateByIdLazyQuery as useGetLazyQuery,
  SubmitContractsPriceTemplate,
  ContractsPriceTemplates
} from "@graphql";
import { useFormStore } from "@modules/formx-react/useFormStore";
import {
  ContractPriceTemplateFormInput,
  ContractPriceTemplateRecordInput
} from "@config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { store } from "@store";
import { useNavigation } from "@navigation";
import { createObjectID } from "@bonliva-crm/common";
import { mergeHighHolidays } from "@config_editor/ContractsPriceTemplates/utils";
import {
  timeReportService,
  ValidateCustomerContractOptionResult
} from "@api/timereports";
import { mapPriceRecord } from "@contracts/CustomerContractPriceListEditor/CustomerContractPriceListFormStore";

export function useContractsPriceTemplatesForm(id) {
  const navigation = useNavigation();

  const [validationResult, setValidationResult] = useState<
    ValidateCustomerContractOptionResult[]
  >([]);

  const validate = (records) => {
    timeReportService
      .validateCustomerContractOptions({
        customerContractPriceRecordDto: records.map(mapPriceRecord)
      })
      .then((result) => {
        setValidationResult(result.data);
      })
      .catch((e) => {
        setValidationResult([]);
      });
  };

  const validateDebounced = debounce(validate, 1000, { leading: false, trailing: true });

  const formStore = useFormStore<ContractPriceTemplateFormInput>({
    classValidatorClassType: ContractPriceTemplateFormInput,
    onSubmit: () => onSubmit(),
    initialValues: new ContractPriceTemplateFormInput(),
    exposeAs: "contractPriceTemplateForm",
    onSetValue: ({ store, name }) => {
      if (name.includes("records.")) {
        validateDebounced(store.values.records);
      }
    }
  });

  const [getById, { loading, data }] = useGetLazyQuery({
    variables: { id, skip: id === "new" },
    fetchPolicy: "network-only",
    onError: (err) => {
      console.log(err);
      store().snackbars.error(`Cannot load data`);
    }
  });

  useEffect(() => {
    getById();
    validateDebounced(formStore.values.records);
  }, [id]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (data.contractsPriceTemplateById && data.getHighHolidays) {
      const resetData = ContractPriceTemplateFormInput.CreateFromFragment(
        data.contractsPriceTemplateById
      );
      resetData.highHolidays = mergeHighHolidays(
        data.getHighHolidays,
        resetData.highHolidays
      ) as any;

      formStore.reset(resetData);
      validateDebounced(formStore.values.records);
    }

    const contractHHs = data.contractsPriceTemplateById?.highHolidays ?? [];

    // avoid removed holidays
    const merged = mergeHighHolidays(data.getHighHolidays, contractHHs);
    formStore.setValue("highHolidays", merged);
    formStore.reset(formStore.values);
  }, [data]);

  const duplicateRecord = (item: ContractPriceTemplateRecordInput) => {
    const temp = { ...item };
    const newID = createObjectID();
    temp._id = newID;
    temp.id = newID;
    formStore.push("records", temp);
    formStore.validate();
  };

  const addBlankRecord = () => {
    formStore.values.records.push(ContractPriceTemplateRecordInput.CreateBlank());
    formStore.validate();
  };

  const onSubmit = () => {
    const formData = formStore.values.getPlain();
    return SubmitContractsPriceTemplate.func(
      {
        id: id === "new" ? null : id,
        data: formData
      },
      { refetchQueries: [ContractsPriceTemplates.OperationName] }
    ).then(({ data }) => {
      // OK and navigate
      if (isEmpty(data.submitContractPriceTemplateForm.errors)) {
        navigation.unBlock("contract_price_template_form");
        navigation.close();

        return;
      }

      store().snackbars.error(
        `Cannot save form\nReason: ${data.submitContractPriceTemplateForm.errors.join(
          "\n"
        )}`
      );
    });
  };

  return {
    formStore,
    loading,
    duplicateRecord,
    addBlankRecord,
    validationResult,
    navigation
  };
}
